<script setup lang="ts">
import { computed, useSlots, useTemplateRef } from 'vue'
import AppBreadcrumb from '../../primitives/AppBreadcrumb/AppBreadcrumb.vue'
import AppScrollArea from '../../primitives/AppScrollArea/AppScrollArea.vue'
import type { BreadcrumbItem } from '../../primitives/AppBreadcrumb/breadcrumb-types'
import { usePageScrollHost } from '@/composables/usePageScroll'
import AppPageIntro from './AppPageIntro.vue'
import type { AppPageTab, AppPageTitlePlacement } from './app-page-types'

const props = withDefaults(
  defineProps<{
    /** The page's `<h1>` text; defaults to the last crumb. A top-level page shows it on the top line, a detail page large below the trail. */
    title?: string
    /** Breadcrumb trail (root → current). One crumb is a top-level page; more make a detail page whose last crumb is the current item. */
    breadcrumbs?: readonly BreadcrumbItem[]
    /** One sentence saying what the page is for: the lead on a top-level page, the line under the title on a detail page. Also available as the `description` slot. */
    description?: string
    /** Detail pages only, where the item's `<h1>` lives: `below` large under the trail · `trail` the trail's current crumb · `body` the page body draws its own. */
    titlePlacement?: AppPageTitlePlacement
    /** Render full-bleed: no frame padding, no header, body fills the shell inset edge-to-edge. Otherwise header and body sit in the one centred page column. */
    bleed?: boolean
    /** The body fills the page below a fully pinned header and owns its scrolling with its own `AppScrollArea`s; without it the body scrolls in the page's scroll area. */
    fullHeight?: boolean
    /** The body spans the whole shell inset instead of the centred page column; for editors that need every pixel of width. */
    wide?: boolean
    /** Documents only: leaves an empty run below the body so a section index can scroll its target to the top. */
    scrollTail?: boolean
    /** Ids of ancestor crumbs that accept a dragged item right now; empty means the trail is inert. */
    dropTargetCrumbIds?: readonly string[]
    /** Level-3 tab bar under the title: each tab is a route, the active one read from the route name, and picking one pushes its `to`. Omit for a page with no sub-pages. */
    tabs?: readonly AppPageTab[]
    /** Accessible name for the tab bar's radiogroup. Required when `tabs` is set. */
    tabsAriaLabel?: string
    /** The selected tab's id, for tabs that share one route; defaults to the tab whose route is the current one. */
    activeTab?: string
  }>(),
  {
    description: undefined,
    titlePlacement: 'below',
    bleed: false,
    fullHeight: false,
    wide: false,
    scrollTail: false,
    dropTargetCrumbIds: () => [],
    tabs: undefined,
    tabsAriaLabel: undefined,
    activeTab: undefined,
  },
)

defineEmits<{
  navigate: [item: BreadcrumbItem]
  crumbDrop: [item: BreadcrumbItem]
}>()

defineSlots<{
  /** The page body. */
  default(): unknown
  /** Controls at the end of the top line. */
  actions?: () => unknown
  /** The description, in place of the `description` prop. */
  description?: () => unknown
  /** A detail page's controls beside its title. */
  'title-actions'?: () => unknown
  /** A detail page's meta line under its title. */
  meta?: () => unknown
  /** A list screen's segmented control or filter; when present, the header up to and including it stays pinned and only the body scrolls. */
  pinned?: () => unknown
}>()

const slots = useSlots()

const isDetail = computed<boolean>(() => (props.breadcrumbs?.length ?? 0) > 1)

const heading = computed<string | undefined>(
  () => props.title ?? props.breadcrumbs?.[props.breadcrumbs.length - 1]?.label,
)

const introTitle = computed<string | undefined>(() =>
  isDetail.value && props.titlePlacement === 'below' ? heading.value : undefined,
)

const columnClass = computed(() =>
  props.bleed ? '' : props.wide ? 'w-full' : 'mx-auto w-full max-w-column',
)

const tailClass = computed(() => {
  if (props.bleed) return ''
  return props.scrollTail ? 'pb-(--document-scroll-tail)' : 'pb-20 sm:pb-24'
})

const hasDescription = (): boolean => Boolean(props.description || slots.description)

const showsDescription = (): boolean =>
  hasDescription() && (!isDetail.value || props.titlePlacement === 'below')

const hasIntro = (): boolean =>
  Boolean(introTitle.value || showsDescription() || props.tabs?.length)

const hasHeader = (): boolean =>
  !props.bleed &&
  Boolean(heading.value || hasDescription() || slots.actions || slots.pinned || props.tabs?.length)

const pinsIntro = (): boolean =>
  props.fullHeight || Boolean(slots.pinned) || Boolean(props.tabs?.length)

const introScrolls = (): boolean => hasHeader() && hasIntro() && !pinsIntro()

const introProps = () => ({
  title: introTitle.value,
  showDescription: showsDescription(),
  description: props.description,
  tabs: props.tabs,
  tabsAriaLabel: props.tabsAriaLabel,
  activeTab: props.activeTab,
})

const frameClass = (): string => {
  if (props.bleed) return 'flex min-h-0 flex-1 flex-col'
  if (props.fullHeight) return 'page-frame flex min-h-0 flex-1 flex-col gap-8 pt-6 pb-8'
  return `page-frame flex min-h-0 flex-1 flex-col pt-6 ${introScrolls() ? 'gap-8' : 'gap-16'}`
}

const area = useTemplateRef<InstanceType<typeof AppScrollArea>>('area')
usePageScrollHost(computed(() => area.value?.element ?? null))
</script>

<template>
  <div :class="frameClass()">
    <header v-if="hasHeader()" class="shrink-0">
      <div class="flex flex-col" :class="columnClass">
        <div class="page-top-line flex h-16 min-w-0 items-center">
          <AppBreadcrumb
            v-if="isDetail && breadcrumbs"
            class="flex-1"
            :items="breadcrumbs"
            :drop-target-ids="dropTargetCrumbIds"
            :current-as="titlePlacement === 'trail' ? 'h1' : 'span'"
            @navigate="$emit('navigate', $event)"
            @item-drop="$emit('crumbDrop', $event)"
          />
          <h1 v-else class="min-w-0 truncate text-body-sm-medium text-text-primary">
            {{ heading }}
          </h1>
          <div v-if="slots.actions" class="ms-auto flex shrink-0 items-center gap-4 ps-8">
            <slot name="actions" />
          </div>
        </div>
        <AppPageIntro v-if="pinsIntro() && hasIntro()" class="mt-8" v-bind="introProps()">
          <template v-if="slots.description" #description><slot name="description" /></template>
          <template v-if="slots.meta" #meta><slot name="meta" /></template>
          <template v-if="slots['title-actions']" #actions><slot name="title-actions" /></template>
        </AppPageIntro>
        <div v-if="slots.pinned" class="mt-8 flex flex-col empty:hidden">
          <slot name="pinned" />
        </div>
      </div>
    </header>
    <div v-else-if="!bleed" class="h-16 shrink-0" aria-hidden="true" />
    <div v-if="fullHeight" class="flex min-h-0 flex-1 flex-col" :class="columnClass">
      <slot />
    </div>
    <div v-else class="flex min-h-0 flex-1 flex-col" :class="columnClass">
      <AppScrollArea
        ref="area"
        :corners="bleed ? 'none' : 'card'"
        class="page-scroller flex min-h-0 flex-1 flex-col"
      >
        <div class="flex flex-1 flex-col gap-16" :class="tailClass">
          <AppPageIntro v-if="introScrolls()" v-bind="introProps()">
            <template v-if="slots.description" #description><slot name="description" /></template>
            <template v-if="slots.meta" #meta><slot name="meta" /></template>
            <template v-if="slots['title-actions']" #actions
              ><slot name="title-actions"
            /></template>
          </AppPageIntro>
          <div class="flex flex-1 flex-col">
            <slot />
          </div>
        </div>
      </AppScrollArea>
    </div>
  </div>
</template>
